import { useCallback, useEffect, useMemo, useState } from 'react';
import { z } from 'zod';
import useCommonState, {
  type BaseStateProps,
  type MobxProps,
  baseValidationSchema,
  type PersonaTitleFormPayload,
  CommonFormProps,
} from './useCommonState';
import { type Inputs } from './Input';
import type { PersonaRowData } from 'admin/types';

const titleValidationSchema = baseValidationSchema.extend({
  titles: z.array(z.string()).min(1, 'Select at least one Title.'),
});

const useTitlePersona = ({
  selectedPersona,
  setToastMessageData,
  isPersonaMessageTtlAndPinLockEnabled,
  loadTitles,
  editMode,
  setSelectedPersona,
  updatePersona,
  createPersona,
  setPersonaFormState,
}: BaseStateProps & Omit<MobxProps, 'findSCIMGroups'> & CommonFormProps) => {
  const {
    commonFormFields,
    setCommonFormFields,
    setLoading,
    selectedOrganizationId,
    setTemplate,
    loading,
    userPermissions,
    setUserPermissions,
    userSettings,
    setUserSettings,
    canRevert,
    errors,
    revertChanges,
    setErrors,
    validateForm,
    template,
  } = useCommonState<'title'>({
    selectedPersona,
    setToastMessageData,
    isPersonaMessageTtlAndPinLockEnabled,
  });
  const [titlesState, setTitlesState] = useState<{
    titles: string[];
    selectedTitles: string[];
  }>({
    titles: [],
    selectedTitles: [],
  });
  const [titlesLoaded, setTitlesLoaded] = useState(false);

  const loadRequirements = useCallback(async () => {
    setLoading(true);
    try {
      const foundTitles = await loadTitles(
        selectedOrganizationId,
        selectedPersona?.personaId || ''
      );
      setTitlesState((prev) => ({
        ...prev,
        titles: foundTitles,
        selectedTitles: selectedPersona?.titles || [],
      }));
    } finally {
      setLoading(false);
      setTitlesLoaded(true);
    }
  }, [
    loadTitles,
    selectedOrganizationId,
    selectedPersona?.titles,
    setLoading,
    selectedPersona?.personaId,
  ]);

  useEffect(() => {
    loadRequirements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNameChange = useCallback(
    (value: string) => {
      setCommonFormFields((currentValues) => ({ ...currentValues, name: value }));
    },
    [setCommonFormFields]
  );

  const handleDescriptionChange = useCallback(
    (value: string) => {
      setCommonFormFields((currentValues) => ({ ...currentValues, description: value }));
    },
    [setCommonFormFields]
  );

  const handleTemplateChange = useCallback(
    (template: PersonaRowData) => {
      setTemplate(template);
    },
    [setTemplate]
  );

  const titlesKey = JSON.stringify(titlesState.titles);
  const titleOptions = useMemo(
    () => titlesState.titles.map((title) => ({ label: title, value: title })),
    // Content-based dep to avoid new array ref from loadRequirements causing schema re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [titlesKey]
  );

  const isLoading = loading || !titlesLoaded;
  const schema = useMemo(() => {
    if (isLoading) return [];
    const schema: Inputs[] = [
      {
        label: 'Name: ',
        type: 'input',
        optionName: 'name',
        dataTestId: 'newPersonaId',
        onInputChange: handleNameChange,
        disabled: !editMode,
        id: 'name',
        value: commonFormFields.name,
      },
      {
        label: 'Description: ',
        type: 'input',
        optionName: 'description',
        dataTestId: 'persona description',
        onInputChange: handleDescriptionChange,
        disabled: !editMode,
        id: 'description',
        value: commonFormFields.description || '',
      },
      {
        label: 'Title: ',
        optionName: 'titles',
        type: 'multiSelect',
        listOptions: titleOptions,
        dataTestId: 'titleDropdown',
        onInputChange: (value: string[]) => {
          setTitlesState((prev) => ({ ...prev, selectedTitles: value }));
        },
        disabled: !editMode,
        id: 'titles',
        value: titlesState.selectedTitles,
        placeholder: 'Select titles...',
      },
      {
        type: 'select',
        label: 'Template: ',
        optionName: 'template',
        dataTestId: 'templateDropdown',
        onInputChange: handleTemplateChange,
        disabled: !editMode,
        id: 'template',
        placeholder: 'Select a template...',
        skip: !!selectedPersona,
      },
    ];
    return schema;
  }, [
    isLoading,
    handleNameChange,
    editMode,
    commonFormFields.name,
    commonFormFields.description,
    handleDescriptionChange,
    titleOptions,
    titlesState.selectedTitles,
    handleTemplateChange,
    selectedPersona,
  ]);

  const savePersona = async (persona: PersonaTitleFormPayload) => {
    try {
      const baseFieldsValues = {
        name: persona.name || '',
        description: persona.description || '',
        titles: persona.titles || [],
        personaType: 'TITLE' as const,
      };
      if (selectedPersona) {
        await updatePersona(
          {
            ...baseFieldsValues,
            permissions: userPermissions,
            settings: userSettings,
          },
          selectedPersona.personaId,
          selectedOrganizationId
        );
        setSelectedPersona(undefined);
        setToastMessageData({ type: 'SUCCESS', message: 'Persona updated' });
      } else {
        await createPersona(
          {
            ...baseFieldsValues,
            parentPersonaId: template?.personaId,
            permissions: userPermissions,
            settings: userSettings,
          },
          selectedOrganizationId
        );
        setToastMessageData({ type: 'SUCCESS', message: 'Persona created' });
      }
      setPersonaFormState({ display: false, type: 'title' });
      setErrors({});
    } catch (error) {
      let errorMessage = 'Response error please try again.';
      if (error?.response?.status === 409) {
        errorMessage = error?.response?.text;
      }
      setErrors((prev) => ({ ...prev, general: errorMessage }));
      setToastMessageData({ type: 'FAILURE', message: errorMessage });
    }
  };

  const onSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const success = validateForm(
      {
        name: commonFormFields.name,
        description: commonFormFields.description,
        titles: titlesState.selectedTitles,
      },
      titleValidationSchema
    );
    if (!success) return;
    savePersona({
      name: commonFormFields.name,
      description: commonFormFields.description,
      titles: titlesState.selectedTitles,
    });
  };

  return {
    canRevert,
    errors,
    loading: isLoading,
    onSave,
    revertChanges,
    schema,
    selectedTitles: titlesState.selectedTitles,
    setUserPermissions,
    setUserSettings,
    userPermissions,
    userSettings,
  };
};

export default useTitlePersona;
